import { novoId } from '../id'
import { useSyncExternalStore } from 'react'
import type { QueryClient } from '@tanstack/react-query'
import { api, ErroRequisicao } from '../api/cliente'
import { chavesChat } from '../api/hooks-chat'

/**
 * A fila dos 5 segundos antes de a mensagem sair para o cliente.
 *
 * Clicar em "Enviar" não manda nada: agenda. Durante 5 segundos a mensagem
 * espera aqui, com um botão de cancelar, e só então vai para a api.
 *
 * **Mora num módulo, e não num componente.** Estado de componente morre quando
 * o componente sai da tela: trocar de conversa desmontaria a conversa, e a
 * mensagem agendada iria junto, calada, sem ser enviada nem cancelada. Aqui ela
 * sobrevive à troca de conversa e à troca de página — o pedido era exatamente
 * esse: o relógio continua, e quando acaba a mensagem sai normalmente.
 *
 * Três cuidados que não aparecem na tela:
 *
 * - **Ordem.** Duas mensagens seguidas para o mesmo cliente saem na ordem em
 *   que foram escritas, mesmo que a primeira demore a responder: os envios de
 *   uma conversa entram numa corrente, um depois do outro.
 * - **Fechar a aba.** A mensagem que ainda esperava sai na hora, em vez de
 *   sumir. Quem fecha a aba não vai cancelar, e perder em silêncio o que a
 *   pessoa mandou é pior do que perder a chance de desistir.
 * - **Sair do sistema.** Mesma coisa, só que antes de derrubar a sessão —
 *   depois dela o servidor recusaria o envio.
 */

export const SEGUNDOS_PARA_CANCELAR = 5

export type EstadoDoEnvio = 'aguardando' | 'enviando' | 'falhou'

/**
 * A mensagem que o envio responde. Vai com ele para a fila: quem cancela e
 * corrige quer continuar respondendo à mesma mensagem, não perder a citação.
 */
export interface CitacaoDoEnvio {
  mensagemId: string
  trecho: string
  daEmpresa: boolean | null
  /** Como a faixa em cima da caixa de escrever chama quem escreveu a citada. */
  quem: string
}

export interface EnvioPendente {
  id: string
  chamadoId: string
  conteudo: string
  respondendoA: CitacaoDoEnvio | null
  /** Quando sai, em milissegundos desde a época. */
  saiEm: number
  estado: EstadoDoEnvio
  erro: string | null
}

let pendentes: EnvioPendente[] = []
const ouvintes = new Set<() => void>()
const relogios = new Map<string, number>()
/** Um envio por vez em cada conversa, na ordem em que foram pedidos. */
const correntes = new Map<string, Promise<void>>()
let consultas: QueryClient | null = null

function publicar(novos: EnvioPendente[]) {
  pendentes = novos
  for (const ouvinte of ouvintes) ouvinte()
}

function atualizar(id: string, mudanca: Partial<EnvioPendente>) {
  publicar(pendentes.map((e) => (e.id === id ? { ...e, ...mudanca } : e)))
}

function retirar(id: string) {
  publicar(pendentes.filter((e) => e.id !== id))
}

/** A fila precisa do cliente de consultas para a conversa se atualizar depois do envio. */
export function configurarFilaDeEnvio(cliente: QueryClient) {
  consultas = cliente
}

export function agendarEnvio(
  chamadoId: string,
  conteudo: string,
  respondendoA: CitacaoDoEnvio | null = null,
): string {
  const id = novoId()
  publicar([
    ...pendentes,
    {
      id,
      chamadoId,
      conteudo,
      respondendoA,
      saiEm: Date.now() + SEGUNDOS_PARA_CANCELAR * 1000,
      estado: 'aguardando',
      erro: null,
    },
  ])
  relogios.set(
    id,
    window.setTimeout(() => despachar(id), SEGUNDOS_PARA_CANCELAR * 1000),
  )
  return id
}

/**
 * Cancela, se ainda der tempo. Devolve a mensagem cancelada, para o texto
 * voltar ao campo — quem cancela quase sempre quer corrigir, não apagar.
 *
 * Depois que o envio começou não há volta: a mensagem já pode ter chegado ao
 * WhatsApp, e fingir que cancelou seria pior do que dizer que já foi.
 */
export function cancelarEnvio(id: string): EnvioPendente | null {
  const envio = pendentes.find((e) => e.id === id)
  if (!envio || envio.estado !== 'aguardando') return null

  const relogio = relogios.get(id)
  if (relogio !== undefined) window.clearTimeout(relogio)
  relogios.delete(id)
  retirar(id)

  // Regra 3: cancelar também é ação de usuário. Só o tamanho vai para o log.
  void api(`/api/chamados/${envio.chamadoId}/envio-cancelado`, {
    metodo: 'POST',
    corpo: { caracteres: envio.conteudo.length },
  }).catch(() => undefined)

  return envio
}

/** Tira da tela um envio que falhou, sem tentar de novo. */
export function descartarEnvio(id: string) {
  const envio = pendentes.find((e) => e.id === id)
  if (envio?.estado === 'falhou') retirar(id)
}

/** Tenta de novo na hora, sem outros 5 segundos: a pessoa acabou de decidir. */
export function reenviar(id: string) {
  const envio = pendentes.find((e) => e.id === id)
  if (envio?.estado !== 'falhou') return
  atualizar(id, { estado: 'aguardando', erro: null })
  despachar(id)
}

function despachar(id: string) {
  relogios.delete(id)
  const envio = pendentes.find((e) => e.id === id)
  if (!envio || envio.estado !== 'aguardando') return

  atualizar(id, { estado: 'enviando' })

  const anterior = correntes.get(envio.chamadoId) ?? Promise.resolve()
  const esta = anterior.then(() => enviarAgora(envio))
  correntes.set(envio.chamadoId, esta)
  void esta.finally(() => {
    if (correntes.get(envio.chamadoId) === esta) correntes.delete(envio.chamadoId)
  })
}

function corpoDoEnvio(envio: EnvioPendente) {
  return {
    conteudo: envio.conteudo,
    notaInterna: false,
    ...(envio.respondendoA ? { respondendoA: envio.respondendoA.mensagemId } : {}),
  }
}

async function enviarAgora(envio: EnvioPendente) {
  try {
    await api(`/api/chamados/${envio.chamadoId}/mensagens`, {
      metodo: 'POST',
      corpo: corpoDoEnvio(envio),
    })
    retirar(envio.id)
    if (consultas) {
      void consultas.invalidateQueries({ queryKey: ['conversas'] })
      void consultas.invalidateQueries({ queryKey: chavesChat.mensagens(envio.chamadoId) })
      void consultas.invalidateQueries({ queryKey: chavesChat.chamado(envio.chamadoId) })
    }
  } catch (erro) {
    /*
      A mensagem que falhou não some.

      O texto já saiu do campo quando a pessoa clicou em enviar. Sem este
      cartão de falha, um WhatsApp desconectado engolia a mensagem: o campo
      vazio dizia "foi", e o cliente nunca recebia.
    */
    atualizar(envio.id, {
      estado: 'falhou',
      erro: erro instanceof ErroRequisicao ? erro.corpo.mensagem : 'Não foi possível enviar.',
    })
  }
}

/**
 * Manda na hora tudo o que ainda esperava. Usado antes de sair do sistema,
 * enquanto a sessão ainda vale.
 */
export async function enviarPendentesAgora(): Promise<void> {
  const aguardando = pendentes.filter((e) => e.estado === 'aguardando')
  for (const envio of aguardando) {
    const relogio = relogios.get(envio.id)
    if (relogio !== undefined) window.clearTimeout(relogio)
    despachar(envio.id)
  }
  await Promise.allSettled([...correntes.values()])
}

/*
  Fechar a aba ou recarregar: o que esperava sai na hora.

  `keepalive` é o que deixa o pedido terminar depois que a página já foi
  embora; um `fetch` comum seria cortado junto com ela. Não passa pela
  corrente de ordem porque não há tempo de esperar resposta — saem na ordem em
  que foram agendados, que é a ordem em que a pessoa escreveu.
*/
if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => {
    for (const envio of pendentes.filter((e) => e.estado === 'aguardando')) {
      const relogio = relogios.get(envio.id)
      if (relogio !== undefined) window.clearTimeout(relogio)
      relogios.delete(envio.id)
      void fetch(`/api/chamados/${envio.chamadoId}/mensagens`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(corpoDoEnvio(envio)),
        keepalive: true,
      }).catch(() => undefined)
    }
  })
}

function assinar(ouvinte: () => void) {
  ouvintes.add(ouvinte)
  return () => ouvintes.delete(ouvinte)
}

const SEM_PENDENTES: EnvioPendente[] = []

/** Os envios de uma conversa, na ordem em que foram pedidos. */
export function useEnviosPendentes(chamadoId: string): EnvioPendente[] {
  const todos = useSyncExternalStore(
    assinar,
    () => pendentes,
    () => SEM_PENDENTES,
  )
  return todos.filter((e) => e.chamadoId === chamadoId)
}
